import React from 'react';

export default function Terms() {
  return (
    <section className="mx-auto max-w-5xl px-6 py-16 bg-white">
      {/* Professional Legal Header */}
      <header className="text-center border-b-2 border-slate-300 pb-12 mb-12">
        <h1 className="text-5xl font-bold mb-6 text-slate-900 tracking-tight">TERMS OF SERVICE</h1>
        <div className="text-base text-slate-600 space-y-2 font-medium">
          <p className="text-xl font-semibold text-slate-800">ToolboxLogic, Inc.</p>
          <p className="text-lg">Community Management Platform</p>
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p>
              <span className="font-bold">Effective Date:</span> January 1, 2025
            </p>
            <p>
              <span className="font-bold">Last Updated:</span> September 22, 2025
            </p>
          </div>
        </div>
      </header>

      {/* Legal Document Body */}
      <article className="space-y-12 text-slate-800 leading-relaxed">
        {/* Acceptance Notice */}
        <div className="bg-blue-50 border-2 border-blue-200 p-10 pb-12 rounded-lg border-l-8 border-l-blue-700">
          <h2 className="text-xl font-bold mb-4 text-blue-900 uppercase tracking-wider">
            NOTICE OF ACCEPTANCE
          </h2>
          <p className="text-base text-slate-700 font-medium leading-loose">
            BY ACCESSING OR USING THE TOOLBOXLOGIC COMMUNITY MANAGEMENT PLATFORM, YOU ACKNOWLEDGE
            THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE AND ALL
            APPLICABLE LAWS AND REGULATIONS. IF YOU DO NOT AGREE WITH THESE TERMS, YOU ARE
            PROHIBITED FROM USING OR ACCESSING THIS PLATFORM.
          </p>
        </div>

        {/* Section 1 */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE I<br />
            <span className="text-2xl">DEFINITIONS AND INTERPRETATION</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Section 1.01 Definitions</h3>
              <p className="text-base mb-4">
                The following terms, when used in this Agreement, shall have the meanings set forth
                below:
              </p>

              <div className="space-y-4 ml-4">
                <div className="flex flex-col space-y-1">
                  <p>
                    <span className="font-bold text-slate-900">(a) "Company"</span> means
                    ToolboxLogic, Inc., a Wyoming C-Corporation, and its successors and assigns.
                  </p>
                </div>
                <div className="flex flex-col space-y-1">
                  <p>
                    <span className="font-bold text-slate-900">(b) "Platform"</span> means the
                    community management web application (Progressive Web App), websites, and all
                    related services provided by Company. Platform is accessible via modern web
                    browsers on desktop, mobile, and tablet devices without requiring native mobile
                    app installation.
                  </p>
                </div>
                <div className="flex flex-col space-y-1">
                  <p>
                    <span className="font-bold text-slate-900">(c) "Community Organization"</span>{' '}
                    means any homeowners association, property management company, municipality,
                    condominium association, or similar entity that enters into a Service Agreement
                    for Platform access.
                  </p>
                </div>
                <div className="flex flex-col space-y-1">
                  <p>
                    <span className="font-bold text-slate-900">(d) "User"</span> means any
                    individual authorized to access the Platform through a Community Organization,
                    including residents, board members, administrators, vendors, and other
                    authorized parties.
                  </p>
                </div>
                <div className="flex flex-col space-y-1">
                  <p>
                    <span className="font-bold text-slate-900">(e) "Service Agreement"</span> means
                    the separate commercial contract between Company and a Community Organization
                    governing Platform access, service levels, and fees.
                  </p>
                </div>
                <div className="flex flex-col space-y-1">
                  <p>
                    <span className="font-bold text-slate-900">(f) "Personal Data"</span> means any
                    information relating to an identified or identifiable natural person as defined
                    under applicable privacy laws.
                  </p>
                </div>
                <div className="flex flex-col space-y-1">
                  <p>
                    <span className="font-bold text-slate-900">(g) "Content"</span> means all
                    information, data, text, documents, images, videos, and other materials uploaded
                    to, transmitted through, or stored on the Platform.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 1.02 Rules of Interpretation
              </h3>
              <div className="space-y-2 ml-4">
                <p>
                  <span className="font-bold">(a)</span> Section headings are for convenience only
                  and shall not affect interpretation.
                </p>
                <p>
                  <span className="font-bold">(b)</span> Words importing the singular include the
                  plural and vice versa.
                </p>
                <p>
                  <span className="font-bold">(c)</span> References to statutes include all
                  amendments, modifications, and re-enactments thereof.
                </p>
                <p>
                  <span className="font-bold">(d)</span> "Including" means "including without
                  limitation" unless otherwise specified.
                </p>
                <p>
                  <span className="font-bold">(e)</span> References to "writing" include electronic
                  communications and digital signatures.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 2 */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE II
            <br />
            <span className="text-2xl">PLATFORM ACCESS AND USER ACCOUNTS</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 2.01 Account Registration
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> Platform access requires an active Service
                  Agreement between Company and the applicable Community Organization.
                </p>
                <p>
                  <span className="font-bold">(b)</span> Individual Users must be authorized by
                  their Community Organization and complete the registration process.
                </p>
                <p>
                  <span className="font-bold">(c)</span> Users must provide accurate, current, and
                  complete information during registration and maintain such information.
                </p>
                <p>
                  <span className="font-bold">(d)</span> Users are responsible for maintaining the
                  confidentiality of account credentials and all activities under their account.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 2.02 Access Levels and Permissions
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> Access levels are determined by Community
                  Organizations and may include resident, board member, administrator, vendor, or
                  other designations.
                </p>
                <p>
                  <span className="font-bold">(b)</span> Company reserves the right to verify User
                  authorization and may request documentation from Community Organizations.
                </p>
                <p>
                  <span className="font-bold">(c)</span> Platform features and functionality vary
                  based on service package (Silver, Gold, or Custom) as specified in the applicable
                  Service Agreement.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 2.03 Account Suspension and Termination
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> Company may suspend or terminate User
                  accounts for violation of these Terms or at the request of the Community
                  Organization.
                </p>
                <p>
                  <span className="font-bold">(b)</span> Users may deactivate their accounts at any
                  time, subject to Community Organization policies and legal retention requirements.
                </p>
                <p>
                  <span className="font-bold">(c)</span> Upon termination, Users lose access to
                  Platform services, though certain data may be retained as required by law or
                  Service Agreements.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 3 */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE III
            <br />
            <span className="text-2xl">ACCEPTABLE USE AND CONDUCT</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Section 3.01 Permitted Uses</h3>
              <div className="space-y-2 ml-4">
                <p>Users may use the Platform solely for:</p>
                <p>
                  <span className="font-bold">(a)</span> Community governance and management
                  activities as authorized by their Community Organization;
                </p>
                <p>
                  <span className="font-bold">(b)</span> Communication with other community members
                  and service providers;
                </p>
                <p>
                  <span className="font-bold">(c)</span> Facility booking, maintenance requests, and
                  other community services;
                </p>
                <p>
                  <span className="font-bold">(d)</span> Financial transactions related to community
                  assessments, fees, and approved services; and
                </p>
                <p>
                  <span className="font-bold">(e)</span> Such other purposes as may be authorized in
                  writing by Company.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 3.02 Prohibited Activities
              </h3>
              <div className="space-y-2 ml-4">
                <p>Users shall not:</p>
                <p>
                  <span className="font-bold">(a)</span> Use the Platform for any unlawful purpose
                  or in violation of applicable laws or regulations;
                </p>
                <p>
                  <span className="font-bold">(b)</span> Transmit harmful, offensive, defamatory, or
                  inappropriate content;
                </p>
                <p>
                  <span className="font-bold">(c)</span> Attempt to gain unauthorized access to
                  Platform systems or other user accounts;
                </p>
                <p>
                  <span className="font-bold">(d)</span> Interfere with or disrupt Platform
                  operations or security measures;
                </p>
                <p>
                  <span className="font-bold">(e)</span> Use automated systems to access the
                  Platform without express written consent;
                </p>
                <p>
                  <span className="font-bold">(f)</span> Reverse engineer, decompile, or attempt to
                  extract source code from Platform software; or
                </p>
                <p>
                  <span className="font-bold">(g)</span> Violate the intellectual property rights of
                  Company or third parties.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 4 */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE IV
            <br />
            <span className="text-2xl">INTELLECTUAL PROPERTY RIGHTS</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 4.01 Company Intellectual Property
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> The Platform, including all software,
                  algorithms, user interfaces, and documentation, is proprietary to Company and
                  protected by intellectual property laws.
                </p>
                <p>
                  <span className="font-bold">(b)</span> Company grants Users a limited,
                  non-exclusive, non-transferable license to use the Platform in accordance with
                  these Terms and applicable Service Agreements.
                </p>
                <p>
                  <span className="font-bold">(c)</span> All rights not expressly granted are
                  reserved by Company.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Section 4.02 User Content</h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> Users retain ownership of Content they
                  upload to the Platform, subject to the license granted herein.
                </p>
                <p>
                  <span className="font-bold">(b)</span> By uploading Content, Users grant Company a
                  non-exclusive, royalty-free license to use, store, and transmit such Content as
                  necessary to provide Platform services.
                </p>
                <p>
                  <span className="font-bold">(c)</span> Users represent and warrant that they have
                  all necessary rights to upload Content and that such Content does not violate
                  third-party rights.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 5 - Limitation of Liability */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE V<br />
            <span className="text-2xl">LIMITATION OF LIABILITY AND DISCLAIMERS</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 5.01 Service Disclaimers
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">AS IS BASIS:</span> THE PLATFORM IS PROVIDED "AS IS"
                  AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED,
                  STATUTORY, OR OTHERWISE.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">DISCLAIMED WARRANTIES:</span> COMPANY DISCLAIMS ALL
                  WARRANTIES INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
                  FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND TITLE.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">NO GUARANTEE:</span> Company does not warrant that
                  Platform services will be uninterrupted, error-free, secure, or free from viruses
                  or other harmful components.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">THIRD-PARTY SERVICES:</span> Company disclaims all
                  liability for third-party services, integrations, or content accessible through
                  the Platform.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 5.02 Limitation of Damages
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">EXCLUDED DAMAGES:</span> IN NO EVENT SHALL COMPANY BE
                  LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR
                  EXEMPLARY DAMAGES.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">EXCLUDED LOSSES:</span> This includes but is not
                  limited to damages for loss of profits, revenue, business opportunities, data,
                  goodwill, or other intangible losses.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">CAUSATION IRRELEVANT:</span> These limitations apply
                  regardless of the theory of liability, whether in contract, tort, strict
                  liability, or otherwise.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">KNOWLEDGE OF DAMAGES:</span> Limitations apply even if
                  Company has been advised of the possibility of such damages.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Section 5.03 Liability Cap</h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">MAXIMUM LIABILITY:</span> Company's total aggregate
                  liability for all claims arising from or relating to these Terms or Platform use
                  shall not exceed the greater of (i) $1,000 or (ii) amounts paid by the applicable
                  Community Organization to Company in the twelve (12) months preceding the claim.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">MULTIPLE CLAIMS:</span> Multiple claims shall not
                  expand this limitation; the cap applies to all claims collectively.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">ESSENTIAL REMEDY:</span> These limitations are an
                  essential element of the agreement between Company and Users.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Section 5.04 Force Majeure</h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">EXCUSED PERFORMANCE:</span> Company shall not be
                  liable for any failure or delay in performance due to circumstances beyond its
                  reasonable control.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">FORCE MAJEURE EVENTS:</span> Such circumstances
                  include but are not limited to acts of God, natural disasters, war, terrorism,
                  labor disputes, government actions, internet failures, or pandemics.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">MITIGATION EFFORTS:</span> Company will use
                  commercially reasonable efforts to minimize the impact of force majeure events on
                  Platform services.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">EXTENDED FORCE MAJEURE:</span> If force majeure events
                  continue for more than 60 consecutive days, either party may terminate affected
                  Service Agreements upon written notice.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 6 - Indemnification */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE VI
            <br />
            <span className="text-2xl">INDEMNIFICATION</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 6.01 User Indemnification Obligations
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">GENERAL INDEMNIFICATION:</span> Users agree to
                  indemnify, defend, and hold harmless Company, its affiliates, officers, directors,
                  employees, and agents from and against all claims, damages, losses, costs, and
                  expenses (including reasonable attorneys' fees).
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">COVERED CLAIMS:</span> This indemnification applies to
                  claims arising from: (i) User violation of these Terms; (ii) User Content posted
                  to the Platform; (iii) User violation of any law or third-party rights; (iv) User
                  negligence or misconduct.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">THIRD-PARTY CLAIMS:</span> Users shall indemnify
                  Company against all third-party claims related to User's Platform use or Content
                  submission.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 6.02 Company Indemnification Obligations
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">INTELLECTUAL PROPERTY INDEMNIFICATION:</span> Company
                  will indemnify Users against third-party claims that Platform use infringes valid
                  U.S. patents, copyrights, or trademarks.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">INDEMNIFICATION CONDITIONS:</span> Company's
                  indemnification obligations are conditioned upon: (i) prompt written notice of
                  claims; (ii) Company's sole control of defense and settlement; (iii) User's
                  reasonable cooperation.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">REMEDIES:</span> If Platform use becomes subject to
                  infringement claims, Company may (at its option) obtain usage rights, modify the
                  Platform, or terminate access with refund of prepaid fees.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">EXCLUSIONS:</span> Company indemnification does not
                  apply to claims arising from User modifications, unauthorized use, or combination
                  with third-party services.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 6.03 Indemnification Procedures
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">NOTICE REQUIREMENT:</span> The indemnified party must
                  provide prompt written notice of any claim, though failure to provide prompt
                  notice shall not excuse indemnification obligations except to the extent of
                  material prejudice.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">DEFENSE CONTROL:</span> The indemnifying party shall
                  have the right to control the defense and settlement of indemnified claims using
                  counsel of its choice.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">COOPERATION:</span> The indemnified party shall
                  provide reasonable cooperation and assistance in the defense of claims.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">SETTLEMENT APPROVAL:</span> No settlement affecting
                  the indemnified party's rights may be made without its prior written consent.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 7 - Dispute Resolution and Governing Law */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE VII
            <br />
            <span className="text-2xl">DISPUTE RESOLUTION AND GOVERNING LAW</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 7.01 Governing Law and Jurisdiction
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">GOVERNING LAW:</span> These Terms shall be governed by
                  and construed in accordance with the laws of the State of Wyoming, without regard
                  to conflict of law principles.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">JURISDICTION:</span> Subject to the arbitration
                  provisions below, the state and federal courts located in Cheyenne, Wyoming shall
                  have exclusive jurisdiction over all disputes.
                </p>
                <p>
                  <span className="font-bold">(c)</span> <span className="font-bold">VENUE:</span>{' '}
                  Users consent to personal jurisdiction and venue in Wyoming for all legal
                  proceedings.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">FEDERAL LAW:</span> Federal law shall apply to matters
                  involving intellectual property, privacy, and other areas of federal jurisdiction.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 7.02 Mandatory Arbitration
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">ARBITRATION REQUIREMENT:</span> All disputes arising
                  from or relating to these Terms or Platform use shall be resolved through binding
                  individual arbitration administered by the American Arbitration Association (AAA).
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">ARBITRATION RULES:</span> Arbitration shall be
                  conducted under AAA Commercial Arbitration Rules, or for disputes under $75,000,
                  AAA Consumer Arbitration Rules.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">ARBITRATOR SELECTION:</span> Disputes shall be decided
                  by a single arbitrator selected according to AAA rules.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">ARBITRATION LOCATION:</span> Arbitration hearings
                  shall take place in Cheyenne, Wyoming, or by mutual agreement in the User's home
                  state.
                </p>
                <p>
                  <span className="font-bold">(e)</span>{' '}
                  <span className="font-bold">ARBITRATION FEES:</span> Company will pay all AAA
                  filing fees for claims under $10,000; fees for larger claims shall be allocated
                  per AAA rules.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 7.03 Class Action Waiver
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">INDIVIDUAL CLAIMS ONLY:</span> Users agree that all
                  disputes shall be resolved on an individual basis and waive the right to
                  participate in class actions, collective actions, or representative proceedings.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">NO CLASS ARBITRATION:</span> Arbitration shall be
                  conducted on an individual basis only; class arbitration and consolidation of
                  claims are prohibited.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">SEVERABILITY:</span> If this class action waiver is
                  found unenforceable, the arbitration provision shall remain in effect for
                  individual claims.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 7.04 Exceptions to Arbitration
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">EQUITABLE RELIEF:</span> Either party may seek
                  injunctive or other equitable relief in court to protect intellectual property
                  rights or prevent irreparable harm.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">SMALL CLAIMS:</span> Claims within the jurisdiction of
                  small claims court may be brought in such court instead of arbitration.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">GOVERNMENT ACTIONS:</span> Nothing prevents
                  governmental authorities from bringing enforcement actions in court.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 7.05 Statute of Limitations
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">LIMITATION PERIOD:</span> Any claim or cause of action
                  arising from or related to Platform use or these Terms must be filed within one
                  (1) year after such claim or cause of action arose.
                </p>
                <p>
                  <span className="font-bold">(b)</span> <span className="font-bold">WAIVER:</span>{' '}
                  Failure to file within this period constitutes a waiver of such claim.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 8 - Service Levels and Performance Standards */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE VIII
            <br />
            <span className="text-2xl">SERVICE LEVELS AND PERFORMANCE STANDARDS</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 8.01 Platform Availability
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">COMMERCIALLY REASONABLE EFFORTS:</span> Company will
                  use commercially reasonable efforts to maintain Platform availability, utilizing
                  industry-leading cloud infrastructure providers (AWS, Google Cloud, Cloudflare
                  CDN).
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">NO UPTIME GUARANTEE:</span> Company does NOT guarantee
                  a specific uptime percentage (e.g., "99.5% SLA") due to dependencies on
                  third-party infrastructure beyond Company's control, including but not limited to
                  cloud provider regional outages, DDoS attacks, internet backbone failures, and
                  power grid disruptions.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">SCHEDULED MAINTENANCE:</span> Company may perform
                  scheduled maintenance during off-peak hours with 24-hour advance notice to
                  Community Organization administrators whenever feasible.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">EMERGENCY MAINTENANCE:</span> Unscheduled maintenance
                  may be performed without notice to address security vulnerabilities, critical
                  system issues, or third-party infrastructure failures.
                </p>
                <p>
                  <span className="font-bold">(e)</span>{' '}
                  <span className="font-bold">OUTAGE NOTIFICATION:</span> Company will notify
                  Community Organization administrators of service disruptions exceeding 2 hours via
                  email and platform status page.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 8.02 Performance Standards
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">RESPONSE TIMES:</span> Platform pages shall load
                  within 3 seconds under normal operating conditions.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">SUPPORT RESPONSE:</span> Customer support response
                  times vary by service package tier and issue priority level (Critical, High,
                  Medium, Low) as specified in applicable Service Agreements. Gold package provides
                  faster response times than Silver package.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">CRITICAL ISSUES:</span> Security or system-critical
                  issues receive priority response based on service package tier. Gold package:
                  4-hour response for Critical issues. Silver package: 8-hour response for Critical
                  issues.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">BUSINESS HOURS:</span> Standard support is provided
                  Monday through Friday, 9:00 AM to 5:00 PM Mountain Time, excluding federal
                  holidays.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 8.03 Service Level Exclusions
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">THIRD-PARTY DEPENDENCIES:</span> Platform availability
                  is subject to third-party infrastructure providers (cloud hosting, CDN, DNS
                  services). Company is NOT liable for outages caused by: (i) Cloud provider
                  regional failures (AWS, Google Cloud); (ii) Internet backbone disruptions; (iii)
                  DDoS attacks beyond Company's mitigation capacity; (iv) Power grid failures
                  affecting data centers; (v) Force majeure events.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">USER EQUIPMENT:</span> Service levels do not apply to
                  issues caused by User equipment, software problems, internet connectivity issues,
                  or configuration errors.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">BETA FEATURES:</span> Beta features, preview
                  functionality, and custom integrations are provided "as-is" without availability
                  commitments.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">OPTIONAL HIGH-AVAILABILITY:</span> Community
                  Organizations requiring enhanced uptime may purchase optional High-Availability
                  add-on (multi-region redundancy, 99.9% target) at additional cost—contact sales
                  for pricing.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 9 - Financial Terms and Data Security */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-blue-700 pb-4 uppercase tracking-wide">
            ARTICLE IX
            <br />
            <span className="text-2xl">FINANCIAL TERMS AND DATA SECURITY</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 9.01 Transparent Pricing Structure
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">DUAL COMPONENT BILLING:</span> Service fees consist of
                  (i) Platform Service Fees for software features and support, and (ii)
                  Infrastructure Usage Fees based on actual data consumption.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">USAGE-BASED TRANSPARENCY:</span> Data usage fees are
                  calculated based on actual community usage patterns including storage, bandwidth,
                  media streaming, and specialized infrastructure requirements.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">ESTIMATION DISCLAIMER:</span> Usage estimates provided
                  during onboarding are projections only and not guaranteed fixed costs. Actual
                  usage may vary based on community activity patterns, feature adoption, and content
                  types.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">THIRD-PARTY COST PASS-THROUGH:</span> Infrastructure
                  usage fees represent actual third-party costs beyond Company's control, including
                  cloud storage, bandwidth, content delivery, and compliance infrastructure. Company
                  applies a standard administrative fee to such costs.
                </p>
                <p>
                  <span className="font-bold">(e)</span>{' '}
                  <span className="font-bold">USAGE PROTECTION MEASURES:</span> Company provides
                  usage monitoring, monthly reports, optional spending caps, and automatic
                  notifications for significant usage increases to prevent surprise billing.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 9.02 Payment Processing and Security
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">PCI DSS COMPLIANCE:</span> All payment processing is
                  conducted through PCI DSS Level 1 certified payment processors. Company does not
                  store credit card information on Platform systems.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">ENCRYPTION:</span> All financial data transmission is
                  encrypted using TLS 1.3 or higher encryption standards.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">FRAUD PREVENTION:</span> Company employs advanced
                  fraud detection and prevention measures for all financial transactions.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">PAYMENT METHODS:</span> Accepted payment methods
                  include credit cards, ACH transfers, and other methods approved by Community
                  Organizations.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 9.03 Financial Compliance
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">ANTI-MONEY LAUNDERING:</span> Company maintains
                  anti-money laundering procedures and may request additional verification for large
                  or unusual transactions.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">REPORTING REQUIREMENTS:</span> Company complies with
                  applicable financial reporting and recordkeeping requirements under federal and
                  state law.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">TAX REPORTING:</span> Company may provide tax
                  reporting information as required by law or Community Organization agreements.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">ESCHEATMENT:</span> Unclaimed funds may be subject to
                  state escheatment laws after required holding periods.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 9.04 Data Security Measures
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">SECURITY FRAMEWORK:</span> Company maintains
                  information security programs informed by generally accepted industry practices.
                  Company does not currently hold any third-party security certification or
                  attestation.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">DATA PROTECTION:</span> Personal and financial data is
                  protected through encryption at rest and in transit, access controls, and regular
                  security assessments.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">INCIDENT RESPONSE:</span> Company maintains incident
                  response procedures for security breaches and will notify affected parties as
                  required by applicable law.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">EMPLOYEE TRAINING:</span> Company personnel receive
                  regular security awareness training and background checks as appropriate for their
                  roles.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 9.05 Breach Notification
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">NOTIFICATION TIMELINE:</span> In the event of a data
                  security breach involving personal information, Company will notify affected
                  Community Organizations and Users without unreasonable delay and within 72 hours
                  where required by law.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">NOTIFICATION CONTENT:</span> Breach notifications will
                  include the nature of the breach, types of information involved, remedial actions
                  taken, and contact information for inquiries.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">REGULATORY REPORTING:</span> Company will comply with
                  all applicable breach notification laws and cooperate with regulatory
                  investigations.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">USER RESPONSIBILITIES:</span> Users should promptly
                  report suspected security incidents and cooperate with Company's incident response
                  procedures.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* End of document content */}

        {/* Footer */}
        <div className="mt-16 pt-8 border-t-2 border-slate-300 text-center">
          <p className="text-sm text-slate-600 font-medium">
            © 2025 ToolboxLogic, Inc. All rights reserved.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            This document constitutes a legally binding agreement. Consult legal counsel for
            interpretation questions.
          </p>
        </div>
      </article>
    </section>
  );
}
